import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash, Lightning, PencilSimple, BookOpen, Code, Lightbulb, Translate, MathOperations, EnvelopeSimple } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  coding: Code,
  writing: PencilSimple,
  learning: BookOpen,
  creative: Lightbulb,
  general: Lightning,
};

export default function TemplatesPanel({ open, onClose, onUse }) {
  const [templates, setTemplates] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newPrompt, setNewPrompt] = useState('');
  const [newCat, setNewCat] = useState('general');

  useEffect(() => {
    if (open) loadTemplates();
  }, [open]);

  const loadTemplates = async () => {
    try {
      const { data } = await axios.get(`${API}/templates`);
      setTemplates(data);
    } catch (e) { console.error(e); }
  };

  const createTemplate = async () => {
    if (!newTitle.trim() || !newPrompt.trim()) return;
    try {
      await axios.post(`${API}/templates`, { title: newTitle, prompt: newPrompt, category: newCat });
      setNewTitle(''); setNewPrompt(''); setNewCat('general'); setShowCreate(false);
      loadTemplates();
    } catch (e) { console.error(e); }
  };

  const deleteTemplate = async (id) => {
    try {
      await axios.delete(`${API}/templates/${id}`);
      loadTemplates();
    } catch (e) { console.error(e); }
  };

  const categories = ['all', ...new Set(templates.map(t => t.category))];
  const filtered = filter === 'all' ? templates : templates.filter(t => t.category === filter);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm px-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        data-testid="templates-panel"
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10 }}
          className="bg-white/5 rounded-2xl shadow-xl border border-white/10 max-w-lg w-full max-h-[80vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-white/10">
            <div>
              <h2 className="text-base font-semibold" style={{ fontFamily: 'Clash Display, sans-serif' }}>Prompt Templates</h2>
              <p className="text-xs text-white/40 mt-0.5">Click to use, or create your own</p>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setShowCreate(!showCreate)} className="w-7 h-7 rounded-md flex items-center justify-center text-white/50 hover:text-lime-400 hover:bg-lime-400/10 transition-all" data-testid="create-template-toggle">
                <Plus className="w-4 h-4" weight="bold" />
              </button>
              <button onClick={onClose} className="w-7 h-7 rounded-md flex items-center justify-center text-white/50 hover:text-white/80 hover:bg-white/10 transition-all" data-testid="close-templates">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Create form */}
          <AnimatePresence>
            {showCreate && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="border-b border-white/10 overflow-hidden">
                <div className="p-4 space-y-2.5">
                  <Input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Template name" className="h-9 text-sm rounded-lg border-white/10" data-testid="template-name-input" />
                  <textarea value={newPrompt} onChange={e => setNewPrompt(e.target.value)} placeholder="Prompt text..." rows={2} className="w-full px-3 py-2 text-sm rounded-lg border border-white/10 outline-none resize-none focus:border-lime-400 focus:ring-1 focus:ring-lime-400/30" data-testid="template-prompt-input" />
                  <div className="flex items-center justify-between">
                    <select value={newCat} onChange={e => setNewCat(e.target.value)} className="text-xs px-2 py-1.5 rounded-md border border-white/10 outline-none" data-testid="template-category-select">
                      <option value="general">General</option>
                      <option value="coding">Coding</option>
                      <option value="writing">Writing</option>
                      <option value="learning">Learning</option>
                      <option value="creative">Creative</option>
                    </select>
                    <button onClick={createTemplate} className="px-3 py-1.5 bg-lime-400/100 hover:bg-lime-600 text-white text-xs font-medium rounded-lg transition-colors" data-testid="save-template-button">Save template</button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Category filter */}
          <div className="flex gap-1.5 px-5 pt-3 pb-2 overflow-x-auto">
            {categories.map(c => (
              <button key={c} onClick={() => setFilter(c)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium capitalize transition-all whitespace-nowrap ${filter === c ? 'bg-lime-400/15 text-lime-300' : 'text-white/50 hover:bg-white/10'}`}
                data-testid={`filter-${c}`}>
                {c}
              </button>
            ))}
          </div>

          {/* Templates list */}
          <div className="flex-1 overflow-y-auto p-3">
            <div className="grid grid-cols-2 gap-2">
              {filtered.map((t) => {
                const Icon = CATEGORY_ICONS[t.category] || Lightning;
                return (
                  <button
                    key={t.id}
                    onClick={() => { onUse(t.prompt); onClose(); }}
                    className="group text-left p-3 rounded-lg border border-white/10 hover:border-lime-400/25 hover:bg-lime-400/10 transition-all relative"
                    data-testid={`template-${t.id}`}
                  >
                    <Icon weight="duotone" className="w-4 h-4 text-lime-500 mb-1.5" />
                    <p className="text-[13px] font-medium text-white/90">{t.title}</p>
                    <p className="text-[11px] text-white/40 mt-0.5 line-clamp-2">{t.prompt.slice(0, 60)}...</p>
                    {!t.is_default && (
                      <span role="button" tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); deleteTemplate(t.id); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); deleteTemplate(t.id); } }}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 w-5 h-5 rounded flex items-center justify-center text-white/40 hover:text-red-400 hover:bg-red-400/15 transition-all cursor-pointer"
                        data-testid={`delete-template-${t.id}`}>
                        <Trash className="w-3 h-3" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
